'use client';

import { LayoutQueryResponse } from '@/models/layout';
import { IWarehousingSolutionsResponse } from '@/models/warehousingSolution';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Image from 'next/image';
import Link from 'next/link';
import { ctfImage } from '@/lib/contentful-url';
import { liveSolutions, solutionHref } from '@/lib/routes';

interface IFooter {
	layout: LayoutQueryResponse;
	warehousingSolutions: IWarehousingSolutionsResponse;
}

const LOGIN_URL = 'https://worcs.lindnerlogistics.com/worcs/Login.htm';

const footerLinkClass = ' text-[#F5F5F5] hover:text-[#FFFFFF] hover:underline transition-all';

export default function Footer(props: IFooter) {
	const currentYear = new Date().getFullYear();
	const layout = props.layout?.layoutCollection?.items?.[0];

	return (
		<footer className="relative z-20 bg-[#1A335B] text-white">
			<div className="container pt-16 xl:pt-32">
				<div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
					{/* Brand + socials */}
					<div className="flex flex-col gap-8">
						<Link prefetch={false} href="/" className="inline-block w-fit">
							<Image
								src={ctfImage(layout?.navbarLogo?.url, { width: 340, quality: 80 })}
								sizes="170px"
								height={layout?.navbarLogo?.height ?? 95}
								width={layout?.navbarLogo?.width ?? 405}
								alt="Lindner Logistics logo"
								className="h-auto w-[170px] max-w-full"
							/>
						</Link>
						<div>
							<p className="mb-3 font-bold text-[#F5F5F5]">Follow our socials</p>
							<div className="flex gap-3">
								<Link
									aria-label="Facebook"
									prefetch={false}
									className="flex size-8 items-center justify-center rounded-full bg-white text-primary-600"
									href="https://www.facebook.com/lindnerlogistics/"
									target="_blank"
									rel="noopener noreferrer"
								>
									<FontAwesomeIcon
										icon={['fab', 'facebook-f']}
										className="text-sm"
									/>
								</Link>
								<Link
									aria-label="LinkedIn"
									prefetch={false}
									className="flex size-8 items-center justify-center rounded-full bg-white text-primary-600"
									href="https://www.linkedin.com/company/lindner-logistics-llc/"
									target="_blank"
									rel="noopener noreferrer"
								>
									<FontAwesomeIcon
										icon={['fab', 'linkedin-in']}
										className="text-sm"
									/>
								</Link>
								<Link
									aria-label="Instagram"
									prefetch={false}
									className="flex size-8 items-center justify-center rounded-full bg-white text-primary-600"
									href="https://www.instagram.com/lindnerlogistics/"
									target="_blank"
									rel="noopener noreferrer"
								>
									<FontAwesomeIcon
										icon={['fab', 'instagram']}
										className="text-sm"
									/>
								</Link>
							</div>
						</div>
					</div>

					{/* Visit */}
					<div className="flex flex-col gap-2">
						<p className="text-base font-bold text-[#F5F5F5]">Visit</p>
						{layout?.footerBusinessName && (
							<p className=" text-[#F5F5F5]">{layout.footerBusinessName}</p>
						)}
						{layout?.footerBuildinglocation && (
							<p className=" text-[#F5F5F5]">{layout.footerBuildinglocation}</p>
						)}
						{layout?.footerStreetAddress && (
							<p className=" text-[#F5F5F5]">{layout.footerStreetAddress}</p>
						)}
						{layout?.footerCity && (
							<p className=" text-[#F5F5F5]">{layout.footerCity}</p>
						)}
						{(layout?.footerCounty || layout?.footerPostcode) && (
							<p className=" text-[#F5F5F5]">
								{[layout.footerCounty, layout.footerPostcode]
									.filter(Boolean)
									.join(', ')}
							</p>
						)}
					</div>

					{/* Warehousing Solutions */}
					<div className="flex flex-col gap-2">
						<p className="text-base font-bold text-[#F5F5F5]">Warehousing Solutions</p>
						{liveSolutions(props.warehousingSolutions?.services).map((service) => (
							<Link
								key={service.slug}
								prefetch={false}
								href={solutionHref(service)}
								className={footerLinkClass}
							>
								{service.title}
							</Link>
						))}
					</div>

					{/* Links */}
					<div className="flex flex-col gap-2">
						<p className="text-base font-bold text-[#F5F5F5]">Links</p>
						<Link prefetch={false} href="/about" className={footerLinkClass}>
							About
						</Link>
						<Link prefetch={false} href="/facilities" className={footerLinkClass}>
							Facilities
						</Link>
						<Link prefetch={false} href="/news" className={footerLinkClass}>
							News
						</Link>
						<Link
							prefetch={false}
							href={LOGIN_URL}
							className={footerLinkClass}
							target="_blank"
							rel="nofollow noopener noreferrer"
						>
							Login
						</Link>
						<Link prefetch={false} href="/contact" className={footerLinkClass}>
							Contact Us
						</Link>
					</div>
				</div>
			</div>

			<div className="mt-16 w-full border-t border-[#B4CFF9]" aria-hidden />

			{/* Bottom strip — wider than container columns */}
			<div className="px-4 pt-16 pb-8 sm:px-6 lg:px-10 xl:px-16">
				<div className="flex min-h-8 flex-col gap-4 text-[#D1E1F9] lg:flex-row lg:items-center lg:justify-between">
					<div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center lg:gap-16">
						<p>
							&copy; {currentYear}{' '}
							{layout?.footerCompanyName?.includes('All Rights Reserved')
								? layout.footerCompanyName
								: `${layout?.footerCompanyName ?? 'Lindner Logistics, LLC'}. All Rights Reserved.`}
						</p>
						{layout?.footerLegal && <p>{layout.footerLegal}</p>}
					</div>
					<div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center lg:gap-16">
						<Link
							prefetch={false}
							href="https://and-element.com"
							className={`${footerLinkClass} !text-[#D1E1F9]`}
							target="_blank"
							rel="noopener noreferrer"
						>
							Website by &Element
						</Link>
						<Link
							prefetch={false}
							href="/terms-and-conditions"
							className={`${footerLinkClass} !text-[#D1E1F9]`}
						>
							Terms & Conditions
						</Link>
						<Link
							prefetch={false}
							href="/privacy-policy"
							className={`${footerLinkClass} !text-[#D1E1F9]`}
						>
							Privacy Policy
						</Link>
					</div>
				</div>
			</div>
		</footer>
	);
}
